import React, { useState, useEffect } from 'react';
import { Phone, Clock, X, Filter, ChevronDown, PhoneIncoming, PhoneMissed, Voicemail, CheckCircle, XCircle } from 'lucide-react';
import { callsAPI } from '../services/api';

const formatDuration = (seconds) => {
  if (typeof seconds !== 'number') return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const CallHistory = () => {
  const [calls, setCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async () => {
    try {
      setLoading(true);
      const response = await callsAPI.getAll();
      setCalls(response.data || []);
      setFilteredCalls(response.data || []);
    } catch (error) {
      console.error('Error loading calls:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extract unique agents for the dropdown
  const agents = Array.from(new Set(calls.map(c => c.agent_name))).filter(Boolean);

  useEffect(() => {
    let result = calls;

    if (dateFrom) {
      result = result.filter(c => new Date(c.date) >= new Date(dateFrom));
    }
    
    if (dateTo) {
      const nextDay = new Date(dateTo);
      nextDay.setDate(nextDay.getDate() + 1);
      result = result.filter(c => new Date(c.date) < nextDay);
    }
    
    if (statusFilter) {
      result = result.filter(c => c.status === statusFilter);
    }
    
    if (agentFilter) {
      result = result.filter(c => c.agent_name === agentFilter);
    }

    setFilteredCalls(result);
  }, [calls, dateFrom, dateTo, statusFilter, agentFilter]);

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setStatusFilter('');
    setAgentFilter('');
  };

  const hasActiveFilters = dateFrom || dateTo || statusFilter || agentFilter;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" data-testid="call-history-page">
      {/* Controls Bar */}
      <div className="flex justify-between items-center mb-6">
         <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-800">Call Logs</h2>
            {hasActiveFilters && (
                <span className="ml-4 text-xs font-medium text-teal-700 bg-teal-50 px-2.5 py-1 rounded-full border border-teal-100 flex items-center animate-in fade-in duration-300">
                    Active Filters
                    <button onClick={clearFilters} className="ml-1.5 hover:text-red-500">
                        <X size={12} />
                    </button>
                </span>
            )}
         </div>
         
         <button 
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className={`flex items-center px-4 py-2 border rounded-lg shadow-sm transition-all text-sm font-medium ${
                isFilterOpen 
                ? 'bg-teal-50 border-teal-200 text-teal-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="filter-toggle-btn"
         >
            <Filter size={16} className="mr-2" />
            Filters
            <ChevronDown size={16} className={`ml-2 transition-transform duration-300 ${isFilterOpen ? 'rotate-180' : ''}`} />
         </button>
      </div>

      {/* Collapsible Filter Panel */}
      <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isFilterOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <div className="mb-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">From Date</label>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">To Date</label>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Outcome</label>
                  <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Outcomes</option>
                    <option value="Connected">Connected</option>
                    <option value="Not Connected">Not Connected</option>
                    <option value="Answered">Answered</option>
                    <option value="Missed">Missed</option>
                    <option value="Voicemail">Voicemail</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Agent</label>
                  <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                      value={agentFilter}
                      onChange={(e) => setAgentFilter(e.target.value)}
                  >
                    <option value="">All Agents</option>
                    {agents.map(agent => (
                      <option key={agent} value={agent}>{agent}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
                <button 
                  onClick={clearFilters}
                  className="flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium mr-2"
                >
                  <X size={16} className="mr-2" /> Clear All
                </button>
                <button 
                  onClick={() => setIsFilterOpen(false)}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium shadow-sm"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calls Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
            <span className="text-sm text-gray-500 font-medium">
              Showing {filteredCalls.length} {filteredCalls.length === 1 ? 'record' : 'records'}
            </span>
        </div>
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date & Time</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Duration</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredCalls.length === 0 ? (
               <tr>
                 <td colSpan={5} className="px-6 py-16 text-center text-gray-500">
                   <div className="flex flex-col items-center justify-center">
                     <div className="bg-gray-100 p-3 rounded-full mb-3">
                        <Filter size={24} className="text-gray-400" />
                     </div>
                     <p className="font-medium text-gray-900">No calls found</p>
                     <p className="text-sm mt-1">Try adjusting your filters to see more results.</p>
                     {hasActiveFilters && (
                        <button onClick={clearFilters} className="mt-4 text-teal-600 hover:text-teal-700 text-sm font-medium">
                            Clear all filters
                        </button>
                     )}
                   </div>
                 </td>
               </tr>
            ) : (
              filteredCalls.map(call => (
                <tr key={call.id} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{call.contact_name}</div>
                      <div className="text-sm text-gray-500">{call.phone}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center">
                          <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-bold mr-2">
                              {(call.agent_name || 'U').charAt(0)}
                          </div>
                          {call.agent_name || 'Unknown'}
                      </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(call.date).toLocaleDateString()} 
                      <span className="text-gray-400 ml-1 text-xs">
                        {new Date(call.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 font-mono flex items-center">
                      <Clock size={14} className="mr-1.5 text-gray-400" />
                      {formatDuration(call.duration)}
                  </td>
                  <td className="px-6 py-4">
                    {(call.status === 'Answered' || call.status === 'Connected') && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {call.status === 'Connected' ? <CheckCircle size={12} className="mr-1.5" /> : <PhoneIncoming size={12} className="mr-1.5" />}
                        {call.status}
                      </span>
                    )}
                    {(call.status === 'Missed' || call.status === 'Not Connected') && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                        {call.status === 'Not Connected' ? <XCircle size={12} className="mr-1.5" /> : <PhoneMissed size={12} className="mr-1.5" />}
                        {call.status}
                      </span>
                    )}
                    {call.status === 'Voicemail' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        <Voicemail size={12} className="mr-1.5" />
                        Voicemail
                      </span>
                    )}
                    {!['Answered', 'Missed', 'Voicemail', 'Connected', 'Not Connected'].includes(call.status) && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                        <Phone size={12} className="mr-1.5" />
                        {call.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CallHistory;
